import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';

import AlertBanner from '~/components/core/AlertBanner';
import StepperComponent from '~/components/core/Stepper';
import type { StepProps } from '~/components/core/Stepper/Step';
import { reportAxiosError } from '~/Utils';

import { useAiClaimImportActions } from './hooks/useAiClaimImportActions';
import ImportConfirmationStep from './steps/ImportConfirmationStep';
import SubOrgSelectionStep from './steps/SubOrgSelectionStep';
import type { AiClaimImportStartResponse, SubOrgPreviewRow } from './types';

const Stepper = StepperComponent as typeof StepperComponent & {
  Step: React.ComponentType<StepProps>;
};

interface ImportBySubOrgDialogProps {
  open: boolean;
  onClose: () => void;
  onImportStarted?: () => void;
}

const ImportBySubOrgDialog: React.FC<ImportBySubOrgDialogProps> = ({ open, onClose, onImportStarted }) => {
  const { fetchPreviewBySubOrg, startImportBySubOrgs } = useAiClaimImportActions();

  const [previewRows, setPreviewRows] = useState<SubOrgPreviewRow[]>([]);
  const [selectedSubOrgIds, setSelectedSubOrgIds] = useState<Set<number>>(new Set());
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [startResponse, setStartResponse] = useState<AiClaimImportStartResponse | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    if (open) {
      const loadPreview = async () => {
        setIsPreviewLoading(true);
        try {
          const response = await fetchPreviewBySubOrg();
          setPreviewRows(response.preview_rows);
        } catch (error) {
          await reportAxiosError(error);
        } finally {
          setIsPreviewLoading(false);
        }
      };
      loadPreview();
    }
  }, [open, fetchPreviewBySubOrg]);

  const handleClose = () => {
    setPreviewRows([]);
    setSelectedSubOrgIds(new Set());
    setIsPreviewLoading(false);
    setStartResponse(null);
    setIsStarting(false);
    onClose();
  };

  const handleStartImport = async (): Promise<boolean> => {
    setIsStarting(true);
    try {
      const response = await startImportBySubOrgs(Array.from(selectedSubOrgIds));
      setStartResponse(response);
      onImportStarted?.();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsStarting(false);
    }
  };

  if (!open) return null;

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      <Form>
        <Stepper
          isDialog
          dialogProps={{
            isDialog: true,
            open: true,
            maxWidth: false,
            PaperProps: {
              style: {
                height: '80vh',
                width: '1000px',
              },
            },
            onClose: handleClose,
            title: 'Import by Sub-Organization',
            subheader: (
              <div className="pb-8 pr-24">
                <AlertBanner
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  withIcon
                  title="The claims loading process cannot be easily reverted."
                  note="Once claims are loaded, any changes or updates to them may require manual adjustments. Please review your selection carefully before proceeding."
                />
              </div>
            ),
          }}
          onComplete={handleClose}
          onStepChange={() => {}}
          nextBtnText="Next"
          backBtnText="Back"
          finishBtnText="Close"
          actionBtnText=""
          firstStepBackBtnText="Back"
          firstStepNextBtnText="Start Import"
          contentClassName="flex justify-center p-24"
          headerClassName="hidden"
          stepsContainerClassName="flex-1 flex flex-col"
        >
          <Stepper.Step
            label="Sub-orgs preview"
            onNext={handleStartImport}
            onBack={() => {}}
            disableBack={true}
            disableNext={isPreviewLoading || selectedSubOrgIds.size === 0 || isStarting}
          >
            <SubOrgSelectionStep
              previewRows={previewRows}
              selectedSubOrgIds={selectedSubOrgIds}
              onSelectionChange={setSelectedSubOrgIds}
              isLoading={isPreviewLoading}
              isStarting={isStarting}
            />
          </Stepper.Step>
          <Stepper.Step label="Done" onNext={() => {}} onBack={() => {}} disableBack={true} disableNext={false}>
            <ImportConfirmationStep totalClaims={startResponse?.total_claims ?? 0} />
          </Stepper.Step>
        </Stepper>
      </Form>
    </Formik>
  );
};

export default ImportBySubOrgDialog;
